import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import {
  Database,
  Download,
  Trash2,
  RefreshCw,
  Plus,
  Loader2,
  Mail,
  CheckCircle,
  XCircle,
  HardDrive,
  Calendar,
  AlertTriangle,
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

function formatBytes(bytes) {
  if (bytes === 0) return '0 B';
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
}

function formatDate(isoStr) {
  const d = new Date(isoStr);
  return d.toLocaleString('sq-AL', {
    day: '2-digit', month: '2-digit', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });
}

export default function Backup() {
  const { token } = useAuth();
  const [backups, setBackups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [deletingFile, setDeletingFile] = useState(null);
  const [sendEmail, setSendEmail] = useState(true);
  const [lastResult, setLastResult] = useState(null);

  const fetchBackups = useCallback(async () => {
    try {
      setLoading(true);
      const res = await fetch(`${API_URL}/api/admin/backups`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error('Gabim ngarkimi');
      const data = await res.json();
      setBackups(data.backups || []);
    } catch {
      toast.error('Gabim gjatë ngarkimit të backup-eve');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchBackups();
  }, [fetchBackups]);

  const createBackup = async () => {
    setCreating(true);
    setLastResult(null);
    try {
      const res = await fetch(
        `${API_URL}/api/admin/backups/create?send_email=${sendEmail}`,
        { method: 'POST', headers: { Authorization: `Bearer ${token}` } }
      );
      const data = await res.json();
      setLastResult(data);
      if (data.success) {
        toast.success(`Backup u krijua: ${data.filename}`);
        fetchBackups();
      } else {
        toast.error(data.error || 'Gabim gjatë krijimit');
      }
    } catch {
      toast.error('Gabim i papritur');
    } finally {
      setCreating(false);
    }
  };

  const downloadBackup = (filename) => {
    const url = `${API_URL}/api/admin/backups/download/${encodeURIComponent(filename)}`;
    const a = document.createElement('a');
    a.href = url;
    a.setAttribute('download', filename);
    // Add auth header via fetch + blob
    fetch(url, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => r.blob())
      .then((blob) => {
        const blobUrl = URL.createObjectURL(blob);
        a.href = blobUrl;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(blobUrl);
      })
      .catch(() => toast.error('Gabim gjatë shkarkimit'));
  };

  const deleteBackup = async (filename) => {
    if (!window.confirm(`Jeni të sigurt që dëshironi të fshini "${filename}"?`)) return;
    setDeletingFile(filename);
    try {
      const res = await fetch(
        `${API_URL}/api/admin/backups/${encodeURIComponent(filename)}`,
        { method: 'DELETE', headers: { Authorization: `Bearer ${token}` } }
      );
      const data = await res.json();
      if (data.success) {
        toast.success('Backup u fshi me sukses');
        fetchBackups();
      } else {
        toast.error(data.detail || 'Gabim gjatë fshirjes');
      }
    } catch {
      toast.error('Gabim i papritur');
    } finally {
      setDeletingFile(null);
    }
  };

  return (
    <div className="space-y-6 p-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white flex items-center gap-2">
            <Database className="w-6 h-6 text-blue-400" />
            Backup Databazës
          </h1>
          <p className="text-zinc-400 text-sm mt-1">
            Menaxhoni backup-et e databazës MongoDB
          </p>
        </div>
        <Button
          onClick={fetchBackups}
          variant="outline"
          size="sm"
          className="border-zinc-700 text-zinc-400 hover:text-white"
          data-testid="refresh-backups-btn"
        >
          <RefreshCw className="w-4 h-4 mr-2" />
          Rifresko
        </Button>
      </div>

      {/* Create Backup Card */}
      <Card className="bg-zinc-900/50 border-zinc-800">
        <CardHeader className="pb-3">
          <CardTitle className="text-base text-white flex items-center gap-2">
            <Plus className="w-4 h-4 text-green-400" />
            Krijo Backup të Ri
          </CardTitle>
          <CardDescription>
            Krijon një backup të plotë të databazës dhe opsionalisht e dërgon me email te dev@albmove.al
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center gap-3 py-2 px-3 bg-zinc-800/50 rounded-lg border border-zinc-700">
            <Mail className="w-4 h-4 text-blue-400" />
            <div className="flex-1">
              <p className="text-sm text-white">Dërgo me Email</p>
              <p className="text-xs text-zinc-500">Dërgon backup-in te dev@albmove.al pas krijimit</p>
            </div>
            <label className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={sendEmail}
                onChange={(e) => setSendEmail(e.target.checked)}
                className="sr-only peer"
                data-testid="send-email-toggle"
              />
              <div className="w-11 h-6 bg-zinc-600 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
            </label>
          </div>

          <Button
            onClick={createBackup}
            disabled={creating}
            className="bg-blue-600 hover:bg-blue-700 text-white"
            data-testid="create-backup-btn"
          >
            {creating ? (
              <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Duke krijuar backup...</>
            ) : (
              <><Database className="w-4 h-4 mr-2" /> Krijo Backup Tani</>
            )}
          </Button>

          {/* Last result */}
          {lastResult && (
            <div
              className={`flex items-start gap-3 p-3 rounded-lg border ${
                lastResult.success
                  ? 'bg-green-500/10 border-green-500/30'
                  : 'bg-red-500/10 border-red-500/30'
              }`}
              data-testid="backup-result"
            >
              {lastResult.success ? (
                <CheckCircle className="w-5 h-5 text-green-400 mt-0.5 flex-shrink-0" />
              ) : (
                <XCircle className="w-5 h-5 text-red-400 mt-0.5 flex-shrink-0" />
              )}
              <div className="text-sm">
                {lastResult.success ? (
                  <>
                    <p className="text-green-400 font-medium">{lastResult.message}</p>
                    <p className="text-zinc-400">
                      Skedari: <span className="text-white">{lastResult.filename}</span> ({lastResult.size_mb} MB)
                    </p>
                    {lastResult.email_sent && (
                      <p className="text-blue-400 flex items-center gap-1 mt-1">
                        <Mail className="w-3 h-3" /> Email u dërgua te dev@albmove.al
                      </p>
                    )}
                    {lastResult.email_error && (
                      <p className="text-yellow-400 flex items-center gap-1 mt-1">
                        <AlertTriangle className="w-3 h-3" /> Email dështoi: {lastResult.email_error}
                      </p>
                    )}
                  </>
                ) : (
                  <p className="text-red-400">{lastResult.error}</p>
                )}
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Backup List */}
      <Card className="bg-zinc-900/50 border-zinc-800">
        <CardHeader className="pb-3">
          <CardTitle className="text-base text-white flex items-center gap-2">
            <HardDrive className="w-4 h-4 text-purple-400" />
            Backup-et e Ruajtura
            <span className="ml-auto text-xs font-normal text-zinc-500 bg-zinc-800 px-2 py-0.5 rounded-full">
              {backups.length} skedarë
            </span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-6 h-6 animate-spin text-zinc-500" />
            </div>
          ) : backups.length === 0 ? (
            <div className="text-center py-12">
              <Database className="w-10 h-10 text-zinc-700 mx-auto mb-3" />
              <p className="text-zinc-500">Nuk ka backup të ruajtura.</p>
              <p className="text-zinc-600 text-sm mt-1">Klikoni "Krijo Backup Tani" për të filluar.</p>
            </div>
          ) : (
            <div className="space-y-2" data-testid="backup-list">
              {backups.map((b) => (
                <div
                  key={b.filename}
                  className="flex items-center justify-between py-3 px-4 bg-zinc-800/50 rounded-lg border border-zinc-700/50 hover:border-zinc-600 transition-colors"
                  data-testid={`backup-item-${b.filename}`}
                >
                  <div className="flex items-center gap-3 min-w-0">
                    <Database className="w-4 h-4 text-blue-400 flex-shrink-0" />
                    <div className="min-w-0">
                      <p className="text-sm text-white truncate font-mono">{b.filename}</p>
                      <div className="flex items-center gap-3 mt-0.5">
                        <span className="text-xs text-zinc-500 flex items-center gap-1">
                          <HardDrive className="w-3 h-3" /> {formatBytes(b.size_bytes)}
                        </span>
                        <span className="text-xs text-zinc-500 flex items-center gap-1">
                          <Calendar className="w-3 h-3" /> {formatDate(b.created_at)}
                        </span>
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0 ml-4">
                    <Button
                      size="sm"
                      variant="outline"
                      className="border-zinc-700 text-zinc-300 hover:text-white hover:border-blue-500"
                      onClick={() => downloadBackup(b.filename)}
                      data-testid={`download-backup-${b.filename}`}
                    >
                      <Download className="w-3.5 h-3.5 mr-1.5" />
                      Shkarko
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      className="border-zinc-700 text-zinc-500 hover:text-red-400 hover:border-red-500/50"
                      onClick={() => deleteBackup(b.filename)}
                      disabled={deletingFile === b.filename}
                      data-testid={`delete-backup-${b.filename}`}
                    >
                      {deletingFile === b.filename ? (
                        <Loader2 className="w-3.5 h-3.5 animate-spin" />
                      ) : (
                        <Trash2 className="w-3.5 h-3.5" />
                      )}
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
